import { useEffect, useState } from "react";
import {
  SELLER_COMMUNICATION_QUICK_ACTIONS,
  formatAdminDate,
  formatSellerContactMethod,
  formatSellerContactStatus,
  formatSellerFollowUpPriority,
  sellerContactStatusBadgeClass,
} from "../../../constants/sellerSubmissionAdminConstants";
import {
  fetchAdminSellerSubmissionMessageTemplate,
  recordAdminSellerSubmissionContactAttempt,
  updateAdminSellerSubmissionCommunication,
} from "../../../services/sellerSubmissionAdminApi";

const MESSAGE_TEMPLATE_OPTIONS = [
  { id: "initial_follow_up", label: "Seguimiento inicial" },
  { id: "request_missing_info", label: "Solicitar información faltante" },
  { id: "request_photos", label: "Solicitar fotos" },
  { id: "approved_preparing", label: "Aprobado / preparando publicación" },
  { id: "draft_ready", label: "Borrador listo" },
  { id: "not_a_fit", label: "No procede" },
  { id: "follow_up_reminder", label: "Recordatorio de seguimiento" },
];

function toLocalDateTimeInput(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

function DetailRow({ label, value }) {
  return (
    <p>
      <strong>{label}:</strong> {value ?? "—"}
    </p>
  );
}

/**
 * @param {{
 *   submissionId: string,
 *   communication?: object|null,
 *   seller?: object|null,
 *   canManage?: boolean,
 *   onUpdated?: (submission: object) => void,
 * }} props
 */
export default function SellerSubmissionCommunicationPanel({
  submissionId,
  communication = null,
  seller = null,
  canManage = false,
  onUpdated,
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [notesDraft, setNotesDraft] = useState(communication?.communicationNotes || "");
  const [summaryDraft, setSummaryDraft] = useState(communication?.lastContactSummary || "");
  const [followUpDraft, setFollowUpDraft] = useState(toLocalDateTimeInput(communication?.nextFollowUpAt));
  const [priorityDraft, setPriorityDraft] = useState(communication?.followUpPriority || "normal");
  const [contactMethodDraft, setContactMethodDraft] = useState(communication?.lastContactMethod || "whatsapp");
  const [selectedTemplateId, setSelectedTemplateId] = useState(MESSAGE_TEMPLATE_OPTIONS[0].id);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateLinks, setTemplateLinks] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(false);

  useEffect(() => {
    setNotesDraft(communication?.communicationNotes || "");
    setSummaryDraft(communication?.lastContactSummary || "");
    setFollowUpDraft(toLocalDateTimeInput(communication?.nextFollowUpAt));
    setPriorityDraft(communication?.followUpPriority || "normal");
    setContactMethodDraft(communication?.lastContactMethod || "whatsapp");
  }, [communication]);

  const applySubmission = (data) => {
    if (data?.submission) onUpdated?.(data.submission);
  };

  const runQuickAction = async (contactStatus) => {
    if (!canManage) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await recordAdminSellerSubmissionContactAttempt(submissionId, {
        contactStatus,
        method: contactMethodDraft,
        summary: summaryDraft || undefined,
        nextFollowUpAt: followUpDraft ? new Date(followUpDraft).toISOString() : undefined,
      });
      applySubmission(data);
      setMessage("Contacto registrado.");
    } catch (err) {
      setMessage(err.message || "No pudimos registrar el contacto.");
    } finally {
      setBusy(false);
    }
  };

  const saveCommunication = async (payload) => {
    if (!canManage) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await updateAdminSellerSubmissionCommunication(submissionId, payload);
      applySubmission(data);
      setMessage("Comunicación actualizada.");
    } catch (err) {
      setMessage(err.message || "No pudimos guardar los cambios.");
    } finally {
      setBusy(false);
    }
  };

  const loadTemplate = async (templateId) => {
    setTemplateBusy(true);
    setTemplatePreview(null);
    setTemplateLinks(null);
    try {
      const data = await fetchAdminSellerSubmissionMessageTemplate(submissionId, templateId);
      setTemplatePreview(data.template || null);
      setTemplateLinks(data.outboundLinks || null);
    } catch {
      setTemplatePreview(null);
      setTemplateLinks(null);
    } finally {
      setTemplateBusy(false);
    }
  };

  useEffect(() => {
    if (submissionId && selectedTemplateId) {
      loadTemplate(selectedTemplateId);
    }
  }, [submissionId, selectedTemplateId]);

  const copyTemplate = async () => {
    if (!templatePreview?.body) return;
    try {
      await navigator.clipboard.writeText(templatePreview.body);
      setMessage("Plantilla copiada al portapapeles.");
    } catch {
      setMessage("No pudimos copiar la plantilla.");
    }
  };

  if (!communication) {
    return <p className="admin-table-subtext">Sin datos de comunicación.</p>;
  }

  return (
    <div className="seller-communication-panel">
      <div style={{ marginBottom: "0.75rem" }}>
        <span className={sellerContactStatusBadgeClass(communication.contactStatus)}>
          {formatSellerContactStatus(communication.contactStatus)}
        </span>
        {communication.followUpOverdue ? (
          <span className="lead-badge lead-badge--danger" style={{ marginLeft: "0.5rem" }}>
            Seguimiento vencido
          </span>
        ) : communication.followUpDueToday ? (
          <span className="lead-badge lead-badge--warning" style={{ marginLeft: "0.5rem" }}>
            Seguimiento hoy
          </span>
        ) : null}
        {communication.followUpPriority === "high" ? (
          <span className="lead-badge lead-badge--danger" style={{ marginLeft: "0.5rem" }}>
            Prioridad alta
          </span>
        ) : null}
      </div>

      <DetailRow label="Contacto preferido" value={formatSellerContactMethod(seller?.preferredContactMethod)} />
      <DetailRow label="Teléfono / WhatsApp" value={seller?.phone} />
      <DetailRow label="Correo" value={seller?.email} />
      <DetailRow label="Último contacto" value={formatAdminDate(communication.lastContactedAt)} />
      <DetailRow label="Último método" value={formatSellerContactMethod(communication.lastContactMethod)} />
      <DetailRow label="Intentos de contacto" value={communication.contactAttemptsCount ?? 0} />
      <DetailRow label="Próximo seguimiento" value={formatAdminDate(communication.nextFollowUpAt)} />
      <DetailRow label="Prioridad" value={formatSellerFollowUpPriority(communication.followUpPriority)} />

      {canManage ? (
        <>
          <div style={{ marginTop: "1rem" }}>
            <label>
              Método para registrar contacto
              <select
                value={contactMethodDraft}
                onChange={(event) => setContactMethodDraft(event.target.value)}
                disabled={busy}
                style={{ display: "block", marginTop: "0.25rem" }}
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="phone">Llamada</option>
                <option value="email">Correo</option>
                <option value="other">Otro</option>
              </select>
            </label>
          </div>

          <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {SELLER_COMMUNICATION_QUICK_ACTIONS.map((action) => (
              <button
                key={action.status}
                type="button"
                className="dealer-portal-btn"
                disabled={busy}
                onClick={() => runQuickAction(action.status)}
              >
                {action.label}
              </button>
            ))}
          </div>

          <div style={{ marginTop: "1rem" }}>
            <label>
              Resumen del último contacto
              <textarea
                className="admin-notes-textarea"
                rows={3}
                value={summaryDraft}
                onChange={(event) => setSummaryDraft(event.target.value)}
                disabled={busy}
                placeholder="Qué se discutió con el vendedor."
              />
            </label>
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <label>
              Próximo seguimiento
              <input
                type="datetime-local"
                value={followUpDraft}
                onChange={(event) => setFollowUpDraft(event.target.value)}
                disabled={busy}
                style={{ display: "block", marginTop: "0.25rem" }}
              />
            </label>
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <label>
              Prioridad de seguimiento
              <select
                value={priorityDraft}
                onChange={(event) => setPriorityDraft(event.target.value)}
                disabled={busy}
                style={{ display: "block", marginTop: "0.25rem" }}
              >
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
              </select>
            </label>
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <label>
              Notas de comunicación (internas)
              <textarea
                className="admin-notes-textarea"
                rows={4}
                value={notesDraft}
                onChange={(event) => setNotesDraft(event.target.value)}
                disabled={busy}
                placeholder="Notas internas sobre seguimiento con el vendedor."
              />
            </label>
          </div>

          <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            <button
              type="button"
              className="dealer-portal-btn"
              disabled={busy}
              onClick={() =>
                saveCommunication({
                  communicationNotes: notesDraft,
                  lastContactSummary: summaryDraft,
                  nextFollowUpAt: followUpDraft ? new Date(followUpDraft).toISOString() : null,
                  followUpPriority: priorityDraft,
                  lastContactMethod: contactMethodDraft,
                })
              }
            >
              {busy ? "Guardando…" : "Guardar comunicación"}
            </button>
            <button
              type="button"
              className="dealer-portal-btn"
              disabled={busy}
              onClick={() => saveCommunication({ clearNextFollowUp: true })}
            >
              Limpiar seguimiento
            </button>
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <h4>Plantillas de mensaje</h4>
            <p className="admin-table-subtext">
              Borradores para copiar o abrir en WhatsApp/correo. No se envían automáticamente.
            </p>
            <select
              value={selectedTemplateId}
              onChange={(event) => setSelectedTemplateId(event.target.value)}
              disabled={templateBusy}
              style={{ display: "block", marginTop: "0.25rem", marginBottom: "0.5rem" }}
            >
              {MESSAGE_TEMPLATE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            {templateBusy ? (
              <p className="admin-table-subtext">Cargando plantilla…</p>
            ) : templatePreview ? (
              <>
                <textarea
                  className="admin-notes-textarea"
                  rows={5}
                  readOnly
                  value={templatePreview.body}
                />
                <div style={{ marginTop: "0.5rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  <button type="button" className="dealer-portal-btn" onClick={copyTemplate}>
                    Copiar plantilla
                  </button>
                  {templateLinks?.whatsappUrl ? (
                    <a
                      className="dealer-portal-btn"
                      href={templateLinks.whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir WhatsApp
                    </a>
                  ) : null}
                  {templateLinks?.mailtoUrl ? (
                    <a className="dealer-portal-btn" href={templateLinks.mailtoUrl}>
                      Abrir correo
                    </a>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {communication.lastContactSummary ? (
            <DetailRow label="Resumen" value={communication.lastContactSummary} />
          ) : null}
          {communication.communicationNotes ? (
            <DetailRow label="Notas internas" value={communication.communicationNotes} />
          ) : null}
          <p className="admin-table-subtext">No tienes permiso para editar la comunicación.</p>
        </>
      )}

      {message ? <p className="admin-table-subtext">{message}</p> : null}
    </div>
  );
}
